import { ConfigService } from './config.service';
import { VmSessionData } from './vm-session-manager.service';
import { Log, IShortLogger } from './logger.service';
import { SharedConnectionResponse } from '../models/shared-connection.model';
import { ScreenDimensionsService } from './screen-dimensions.service';
import Guacamole from 'guacamole-common-js';
import { Client } from 'guacamole-common-js/lib/Client';
import { WebSocketTunnel } from 'guacamole-common-js/lib/WebSocketTunnel';
import { Keyboard } from 'guacamole-common-js/lib/Keyboard';
import { Mouse } from 'guacamole-common-js/lib/Mouse';
import { InputStream } from 'guacamole-common-js/lib/InputStream';

/**
 * Individual Guacamole client instance for managing a single VM connection.
 * This class replaces the main.js script functionality with proper TypeScript implementation.
 *
 * Each instance manages:
 * - WebSocket connection to Guacamole proxy
 * - Event handlers for mouse, keyboard, clipboard
 * - File transfer capabilities
 * - Connection lifecycle and cleanup
 *
 * Key features:
 * - Multi-window support (each window gets its own instance)
 * - Proper resource cleanup to prevent memory leaks
 * - Type-safe Guacamole API integration
 * - Clipboard bidirectional support
 * - File download handling
 *
 * Excluded features (for VNC compatibility):
 * - Dynamic resize handling (VNC doesn't support this well)
 * - Window resize event listeners
 * - Optimal screen dimension calculations
 *
 * @class GuacamoleClientInstance
 *
 * @example
 * ```typescript
 * const instance = new GuacamoleClientInstance(
 *   'class-123/user-456',
 *   displayElement,
 *   configService
 * );
 *
 * await instance.initialize(sessionData);
 *
 * // Later cleanup
 * instance.cleanup();
 * ```
 */
export class GuacamoleClientInstance {
  private readonly logger: IShortLogger<GuacamoleClientInstance> = Log.create(GuacamoleClientInstance);
  public client: Client | null = null;
  private tunnel: WebSocketTunnel | null = null;
  private keyboard: Keyboard | null = null;
  private mouse: Mouse | null = null;
  public pasteEventListener: ((event: ClipboardEvent) => void) | null = null;
  private keydownPasteBridgeListener: ((event: KeyboardEvent) => void) | null = null;
  public dragOverListener: ((event: DragEvent) => void) | null = null;
  public dropListener: ((event: DragEvent) => void) | null = null;
  private canvasEnterListener: (() => void) | null = null;
  private canvasLeaveListener: (() => void) | null = null;
  private isConnected = false;
  private isSharedConnection = false; // Track if this is a trainer joining student session

  // ---------------------------------------------------------------------------
  // Reconnect state
  // ---------------------------------------------------------------------------
  private storedSessionData: VmSessionData | null = null;
  private reconnectAttemptCount = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private reconnectTimeoutHandle: any = null;
  /** Set to true before an intentional disconnect to suppress reconnect attempts. */
  private isIntentionalDisconnect = false;
  /** True while we are in a reconnect cycle (i.e. not the first connect). */
  private isInReconnectFlow = false;

  // Callbacks that outer components can attach to for UI updates
  public onReconnecting: ((attempt: number, maxAttempts: number) => void) | null = null;
  public onReconnected: (() => void) | null = null;
  public onConnectionFailed: (() => void) | null = null;

  /**
   * Fired when a drag-and-drop file upload **begins** (browser → VM).
   * Receives the filename so the UI can display a progress indicator.
   */
  public onFileUploadStart: ((filename: string, sizeBytes: number) => void) | null = null;

  /**
   * Fired when a drag-and-drop file upload **completes** successfully.
   * Receives the filename so the UI can dismiss or confirm the indicator.
   */
  public onFileUploadComplete: ((filename: string) => void) | null = null;

  /**
   * Fired when a drag-and-drop file upload **fails** (read error or stream error).
   * Receives the filename so the UI can show an error toast.
   */
  public onFileUploadError: ((filename: string, error: any) => void) | null = null;

  /**
   * Fired when a file download from the VM has started for download.
   * Receives the filename for UI confirmation.
   */
  public onFileDownloadStart: ((filename: string, length: number) => void) | null = null;

  /**
   * Fired when a file download from the VM has been received and saved locally.
   * Receives the filename for UI confirmation.
   */
  public onFileDownloadComplete: ((filename: string) => void) | null = null;

  /** Indicates whether file transfer is allowed for this VM. */
  public isFileTransferAllowed: boolean = true;

  /**
   * Creates a new Guacamole client instance.
   *
   * @param sessionKey - Unique session identifier (e.g., "class-123/user-456")
   * @param displayElement - HTML element to attach the Guacamole display
   * @param configService - Service for getting configuration (WS URL, API URL)
   * @param screenDimensionsService - Service for calculating optimal screen dimensions
   */
  constructor(
    private sessionKey: string,
    private displayElement: HTMLElement,
    private configService: ConfigService,
    private screenDimensionsService: ScreenDimensionsService
  ) {}

  /**
   * Initialize the Guacamole client with session data.
   *
   * @param sessionData - VM session data containing encrypted token and credentials
   * @throws Error if initialization fails
   */
  async initialize(sessionData: VmSessionData): Promise<void> {
    try {
      this.logger.d(`Initializing Guacamole client for session: ${this.sessionKey}`);

      // Persist session data so reconnect attempts can re-use it.
      this.storedSessionData = sessionData;
      if (!this.isInReconnectFlow) {
        // Fresh connection — reset counters so stale state from a previous lifecycle
        // does not interfere.
        this.reconnectAttemptCount = 0;
        this.isIntentionalDisconnect = false;
      }

      // Create WebSocket tunnel
      const wsUrl = this.configService.getWsUrl();
      this.logger.d('Using WebSocket URL:', wsUrl);
      this.tunnel = new Guacamole.WebSocketTunnel(wsUrl);

      // Create client
      this.client = new Guacamole.Client(this.tunnel);

      // Setup all event handlers
      this.setupEventHandlers();
      this.setupClipboard();
      this.setupFileTransfer();
      this.setupFileUpload();

      // Add client display to the page
      this.displayElement.appendChild(this.client.getDisplay().getElement());

      // Get optimal screen dimensions for the connection
      const dimensions = this.screenDimensionsService.getOptimalDimensions();
      this.logger.d(`Using optimal dimensions: ${dimensions.width}x${dimensions.height} @ ${dimensions.dpi} DPI`);

      // Connect with encrypted token and dynamic screen dimensions
      // For RDP, these unencrypted parameters override the encrypted token's dimensions
      // allowing optimal use of available screen real estate
      let connectString = `token=${encodeURIComponent(sessionData.encryptedToken)}`;
      connectString += `&width=${dimensions.width}&height=${dimensions.height}&dpi=${dimensions.dpi}`;

      // Add protocol-specific parameters
      if (sessionData.protocol === 'rdp') {
        connectString += `&GUAC_AUDIO=audio/L16`;
      }

      this.client.connect(connectString);

      this.logger.d(`Guacamole client initialized for session: ${this.sessionKey}`);
    } catch (error) {
      this.logger.e(`Failed to initialize Guacamole client for session ${this.sessionKey}:`, error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * Initialize as a shared connection (trainer joining student's session).
   * This creates a secondary connection that joins an existing RDP session.
   *
   * @param sharedConnectionInfo - Shared connection information from backend
   * @param sessionData - VM session data for connection parameters (protocol, etc.)
   * @throws Error if initialization fails
   */
  async initializeShared(
    sharedConnectionInfo: SharedConnectionResponse,
    sessionData: VmSessionData
  ): Promise<void> {
    try {
      this.logger.d(`Initializing shared Guacamole connection for trainer to join: ${sharedConnectionInfo.sessionKey}`);
      this.isSharedConnection = true;

      // Use configured WebSocket URL
      const wsUrl = this.configService.getWsUrl();
      this.logger.d('Using WebSocket URL for shared connection:', wsUrl);
      this.tunnel = new Guacamole.WebSocketTunnel(wsUrl);

      // Create client
      this.client = new Guacamole.Client(this.tunnel);

      // Setup all event handlers (same as regular connection)
      this.setupEventHandlers();
      this.setupClipboard();
      this.setupFileTransfer();
      this.setupFileUpload();

      // Add client display to the page
      const displayElement = this.client.getDisplay().getElement();
      this.displayElement.appendChild(displayElement);

      // Apply scaling CSS for trainer view
      this.applyTrainerScaling(displayElement);

      // Get optimal dimensions for trainer view (typically larger screen)
      const dimensions = this.screenDimensionsService.getTrainerViewDimensions();
      this.logger.d(`Using trainer view dimensions: ${dimensions.width}x${dimensions.height} @ ${dimensions.dpi} DPI`);

      // Use the encrypted join token with dynamic screen dimensions
      // The backend has already created a token with connection.join structure
      // Format: { connection: { join: "$uuid", settings: { ... } } }
      // We append unencrypted dimensions to optimize trainer's viewing experience
      let connectString = `token=${encodeURIComponent(sharedConnectionInfo.encryptedJoinToken)}`;
      connectString += `&width=${dimensions.width}&height=${dimensions.height}&dpi=${dimensions.dpi}`;

      this.client.connect(connectString);

      this.logger.d(`Shared Guacamole connection initialized for session: ${this.sessionKey}`);
    } catch (error) {
      this.logger.e(`Failed to initialize shared Guacamole connection for session ${this.sessionKey}:`, error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * Apply CSS scaling for trainer view to match student's resolution.
   * Uses CSS transform to scale the canvas to fit trainer's viewport.
   *
   * @private
   * @param displayElement - The Guacamole display element
   */
  private applyTrainerScaling(displayElement: HTMLElement): void {
    // Apply CSS to make the display scale to fit trainer's viewport
    displayElement.style.width = '100%';
    displayElement.style.height = '100%';
    displayElement.style.display = 'flex';
    displayElement.style.justifyContent = 'center';
    displayElement.style.alignItems = 'center';
    displayElement.style.overflow = 'hidden';

    // Wait for canvas to be created and apply scaling
    setTimeout(() => {
      const canvas = displayElement.querySelector('canvas') as HTMLCanvasElement;
      if (canvas) {
        // Store original dimensions
        const originalWidth = canvas.width;
        const originalHeight = canvas.height;

        this.logger.d('Student canvas dimensions:', { width: originalWidth, height: originalHeight });

        // Calculate scale to fit trainer's viewport
        const scaleToFit = () => {
          const containerWidth = this.displayElement.clientWidth;
          const containerHeight = this.displayElement.clientHeight;

          const scaleX = containerWidth / originalWidth;
          const scaleY = containerHeight / originalHeight;
          const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

          canvas.style.transform = `scale(${scale})`;
          canvas.style.transformOrigin = 'center center';

          this.logger.d('Trainer view scaled:', { scale, containerWidth, containerHeight });
        };

        // Apply initial scaling
        scaleToFit();

        // Re-scale on window resize
        const resizeHandler = () => scaleToFit();
        window.addEventListener('resize', resizeHandler);

        // Store cleanup function
        const originalCleanup = this.canvasFixCleanup;
        this.canvasFixCleanup = () => {
          window.removeEventListener('resize', resizeHandler);
          if (originalCleanup) originalCleanup();
        };
      }
    }, 500);
  }

  /**
   * Setup core event handlers for the Guacamole client.
   * Handles connection state changes, errors, and input events.
   *
   * @private
   */
  private setupEventHandlers(): void {
    if (!this.client) return;

    // Error handler — schedule reconnect instead of hard-alerting the user.
    this.client.onerror = (error: any) => {
      this.logger.e(`Guacamole error for session ${this.sessionKey}:`, error);

      const vmLoader = document.getElementById('vm-loader');
      if (vmLoader) { vmLoader.classList.add('loader-hidden'); }

      if (!this.isIntentionalDisconnect) {
        this.scheduleReconnect();
      }
    };

    // State change handler
    this.client.onstatechange = (state: number) => {
      this.logger.d(`Guacamole client state changed for session ${this.sessionKey}:`, state);

      if (state === 3) {
        // CONNECTED — clear reconnect state and notify component
        const wasReconnecting = this.isInReconnectFlow;
        this.isConnected = true;
        this.isInReconnectFlow = false;
        this.reconnectAttemptCount = 0;

        if (wasReconnecting && this.onReconnected) {
          this.onReconnected();
        }

        if (this.client) {
          const display = this.client.getDisplay();
          this.logger.d('Display ready after connection');

          setTimeout(() => {
            const canvas = display.getElement().querySelector('canvas');
            if (canvas) {
              this.logger.d('Canvas found:', {
                width: canvas.width,
                height: canvas.height,
                style: canvas.getAttribute('style'),
                computed: window.getComputedStyle(canvas)
              });
            }
          }, 100);
        }

        setTimeout(() => {
          const vmLoader = document.getElementById('vm-loader');
          if (vmLoader) {
            vmLoader.classList.add('loader-hidden');
            this.logger.d('VM is ready, loader hidden');
          }
        }, 1000);

      } else if (state === 5) {
        // DISCONNECTED — if not intentional, attempt to reconnect
        if (this.isConnected && !this.isIntentionalDisconnect) {
          this.logger.w(`Session ${this.sessionKey} disconnected unexpectedly — scheduling reconnect`);
          this.isConnected = false;
          this.scheduleReconnect();
        }
      }
    };

    // Setup mouse input
    this.mouse = new Guacamole.Mouse(this.client.getDisplay().getElement());
    this.mouse.onEach(['mousedown', 'mouseup', 'mousemove', 'mousewheel'],
      (e: any) => this.client!.sendMouseState(e.state));

    // Suppress host cursor inside the Guacamole display boundary so only
    // the remote VM software cursor (rendered by guacamole-common-js) is visible.
    const displayEl = this.client.getDisplay().getElement();
    this.canvasEnterListener = () => { displayEl.style.cursor = 'none'; };
    this.canvasLeaveListener = () => { displayEl.style.cursor = ''; };
    displayEl.addEventListener('mouseenter', this.canvasEnterListener);
    displayEl.addEventListener('mouseleave', this.canvasLeaveListener);

    // Setup keyboard input
    this.keyboard = new Guacamole.Keyboard(document);
    this.keyboard.onkeydown = (keysym: number) => this.client!.sendKeyEvent(1, keysym);
    this.keyboard.onkeyup = (keysym: number) => this.client!.sendKeyEvent(0, keysym);
  }

  /**
   * Schedule a reconnect attempt with exponential back-off.
   * Capped at MAX_RECONNECT_ATTEMPTS to prevent infinite loops.
   */
  private scheduleReconnect(): void {
    if (this.isIntentionalDisconnect || !this.storedSessionData) return;

    if (this.reconnectAttemptCount >= this.MAX_RECONNECT_ATTEMPTS) {
      this.logger.e(`Max reconnect attempts (${this.MAX_RECONNECT_ATTEMPTS}) reached for session: ${this.sessionKey}`);
      if (this.onConnectionFailed) this.onConnectionFailed();
      return;
    }

    this.reconnectAttemptCount++;
    this.isInReconnectFlow = true;

    // Exponential back-off capped at 30 s
    const delay = Math.min(3000 * this.reconnectAttemptCount, 30_000);

    this.logger.i(`Scheduling reconnect attempt ${this.reconnectAttemptCount}/${this.MAX_RECONNECT_ATTEMPTS} in ${delay}ms for session: ${this.sessionKey}`);

    if (this.onReconnecting) {
      this.onReconnecting(this.reconnectAttemptCount, this.MAX_RECONNECT_ATTEMPTS);
    }

    this.reconnectTimeoutHandle = setTimeout(async () => {
      this.reconnectTimeoutHandle = null;
      try {
        // Tear down the broken connection before re-initialising
        this.cleanupForReconnect();
        // Ensure flag is clear in case cleanup() was called somewhere in the chain
        this.isIntentionalDisconnect = false;
        await this.initialize(this.storedSessionData!);
      } catch {
        // initialize() may have called cleanup() which sets isIntentionalDisconnect;
        // reset it so the next attempt is not blocked.
        this.isIntentionalDisconnect = false;
        this.scheduleReconnect();
      }
    }, delay);
  }

  /**
   * Tear down the current broken connection state without marking the session as
   * intentionally closed.  Used between reconnect attempts.
   */
  private cleanupForReconnect(): void {
    this.logger.d(`cleanupForReconnect for session: ${this.sessionKey}`);

    if (this.reconnectTimeoutHandle) {
      clearTimeout(this.reconnectTimeoutHandle);
      this.reconnectTimeoutHandle = null;
    }

    if (this.client) {
      try { this.client.disconnect(); } catch { /* ignore */ }
    }

    // Remove old display children so initialize() can re-attach a fresh canvas
    while (this.displayElement.firstChild) {
      this.displayElement.removeChild(this.displayElement.firstChild);
    }

    if (this.canvasFixCleanup) {
      this.canvasFixCleanup();
      this.canvasFixCleanup = undefined;
    }

    if (this.pasteEventListener) {
      window.removeEventListener('paste', this.pasteEventListener);
      this.pasteEventListener = null;
    }

    if (this.keydownPasteBridgeListener) {
      document.removeEventListener('keydown', this.keydownPasteBridgeListener, true);
      this.keydownPasteBridgeListener = null;
    }

    if (this.dragOverListener && this.displayElement) {
      this.displayElement.removeEventListener('dragover', this.dragOverListener, true);
      this.dragOverListener = null;
    }

    if (this.dropListener && this.displayElement) {
      this.displayElement.removeEventListener('drop', this.dropListener, true);
      this.dropListener = null;
    }

    const guacDisplayEl = this.client?.getDisplay()?.getElement();
    if (guacDisplayEl && this.canvasEnterListener) {
      guacDisplayEl.removeEventListener('mouseenter', this.canvasEnterListener);
    }
    if (guacDisplayEl && this.canvasLeaveListener) {
      guacDisplayEl.removeEventListener('mouseleave', this.canvasLeaveListener);
    }
    this.canvasEnterListener = null;
    this.canvasLeaveListener = null;

    if (this.keyboard) {
      this.keyboard.onkeydown = null;
      this.keyboard.onkeyup = null;
      this.keyboard.reset();
      this.keyboard = null;
    }

    this.mouse = null;
    this.client = null;
    this.tunnel = null;
    this.isConnected = false;
  }

  /**
   * Send a display resize instruction to the remote VM.
   * Only meaningful for RDP connections; silently no-ops for VNC.
   *
   * @param width  - New display width in pixels
   * @param height - New display height in pixels
   */
  public sendSize(width: number, height: number): void {
    if (this.client && this.isConnected) {
      try {
        (this.client as any).sendSize(width, height);
        this.logger.d(`Display resize sent: ${width}x${height} for session: ${this.sessionKey}`);
      } catch (error) {
        this.logger.w('sendSize failed (may not be supported by protocol):', error);
      }
    }
  }

  /**
   * Manually trigger a reconnect from outside the instance.
   * Used by the component when the window regains focus and the connection
   * appears to have been silently dropped by the browser.
   */
  public triggerReconnect(): void {
    if (this.isIntentionalDisconnect) return;
    this.logger.i(`Manual reconnect triggered for session: ${this.sessionKey}`);
    this.scheduleReconnect();
  }

  /**
   * Setup bidirectional clipboard support.
   * Handles both receiving clipboard data from remote and sending local clipboard data.
   *
   * @private
   */
  private setupClipboard(): void {
    if (!this.client) return;

    // Handle clipboard data from remote session
    this.client.onclipboard = (stream: InputStream, mimetype: string) => {
      let data = '';
      const reader = new Guacamole.StringReader(stream);
      reader.ontext = (text: string) => data += text;
      reader.onend = () => {
        this.logger.d("Clipboard data received:", data);

        // Try modern Clipboard API first
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(data).catch((err) => {
            this.logger.e('Failed to copy to clipboard:', err);
            this.fallbackClipboardCopy(data);
          });
        } else {
          // Fallback for older browsers or non-secure contexts
          this.fallbackClipboardCopy(data);
        }
      };
    };

    // Handle paste events from local clipboard to remote session
    this.pasteEventListener = (event: ClipboardEvent) => {
      const text = event.clipboardData?.getData('text/plain');
      if (text && this.client) {
        event.preventDefault(); // Prevent default paste behavior in browser

        // Send clipboard data to the remote session
        const stream = this.client.createClipboardStream('text/plain');
        const writer = new Guacamole.StringWriter(stream);
        writer.sendText(text);
        writer.sendEnd();
        this.logger.d("Sent clipboard data to remote:", text);
      }
    };

    window.addEventListener('paste', this.pasteEventListener);

    // ── Ctrl+V / Cmd+V keydown bridge ──────────────────────────────────────
    // The window-level `paste` listener above only fires when the browser
    // itself dispatches a synthetic paste event (typically when an editable
    // element is focused). When the Guacamole canvas owns focus, the keystroke
    // is captured by the protocol-level Keyboard handler and forwarded to the
    // remote VM; the browser's paste pipeline never fires, so the local
    // clipboard never reaches the remote session.
    //
    // To paper over this, we additionally listen for the Ctrl+V / Cmd+V
    // keystroke at the document level (capture phase, so we run before
    // Guacamole.Keyboard's handler) and proactively read the local clipboard
    // via the async Clipboard API. The text is streamed to the VM exactly as
    // the paste handler would do, so the user can then paste it inside the VM
    // with another Ctrl+V (which the VM keyboard will see normally).
    //
    // Falls back silently if `navigator.clipboard.readText()` is unavailable
    // (insecure context, permission denied, or browsers without the API).
    this.keydownPasteBridgeListener = (event: KeyboardEvent) => {
      if (!this.client) return;
      // Ignore repeat events generated while the key is held down — we only want
      // to fire once per distinct physical key-press (the `repeat` property is
      // true for auto-repeated keydown events). Without this guard the clipboard
      // text is streamed to the VM on every repeat tick, causing the pasted text
      // to appear multiple times.
      if (event.repeat) return;
      const isPasteKey = event.key === 'v' || event.key === 'V';
      const isModifier = event.ctrlKey || event.metaKey;
      if (!isPasteKey || !isModifier) return;
      if (!navigator.clipboard || typeof navigator.clipboard.readText !== 'function') return;
      if (!window.isSecureContext) return;

      navigator.clipboard.readText().then(text => {
        if (!text || !this.client) return;
        const stream = this.client.createClipboardStream('text/plain');
        const writer = new Guacamole.StringWriter(stream);
        writer.sendText(text);
        writer.sendEnd();
        this.logger.d('Sent clipboard data to remote via Ctrl+V keydown bridge:', text);
      }).catch(err => {
        // Permission may be denied without user gesture — log at debug only.
        this.logger.d('Clipboard readText failed (expected if no permission):', err);
      });
    };
    document.addEventListener('keydown', this.keydownPasteBridgeListener, true);
  }

  /**
   * Fallback clipboard copy method for older browsers.
   *
   * @private
   * @param text - Text to copy to clipboard
   */
  private fallbackClipboardCopy(data: string): void {
    // Update the hidden textarea and trigger copy
    const textarea = document.getElementById('clipboard-textarea') as HTMLTextAreaElement;
    if (textarea) {
      textarea.value = data;
      textarea.select();
      try {
        const successful = document.execCommand('copy');
        const msg = successful ? 'successful' : 'unsuccessful';
        this.logger.d('Copying text command was ' + msg);
      } catch (err) {
        this.logger.e('Failed to copy text: ', err);
      }
      // Deselect the text to avoid visual artifacts
      window.getSelection()?.removeAllRanges();
    }
  }

  /**
   * Setup file transfer support for downloading files from remote session.
   *
   * @private
   */
  private setupFileTransfer(): void {
    this.logger.d(`Setting up file transfer handlers for session: ${this.sessionKey}, isFileTransferAllowed=${this.isFileTransferAllowed}`);
    if (!this.client) return;

    this.client.onfile = (stream: InputStream, mimetype: string, filename: string) => {
      stream.sendAck("Ready", Guacamole.Status.Code.SUCCESS);

      const reader = new Guacamole.BlobReader(stream, mimetype);

      reader.onprogress = (length: number) => {
        this.onFileDownloadStart?.(filename, length);
      };

      reader.onend = () => {
        // Automatically create a link and download the file
        const file = reader.getBlob();
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          this.logger.d(`File download complete: ${filename}`);
          this.onFileDownloadComplete?.(filename);
        }, 100);
      };
    };
  }

  /**
   * Setup drag-and-drop file upload from the browser into the remote VM's
   * mounted drive (RDPDR for RDP, SFTP for VNC when configured).
   *
   * The Guacamole protocol exposes uploads via `client.createFileStream(mime, name)`
   * which returns a writable {@link OutputStream}. We chunk the file as
   * ArrayBuffers and feed it through {@link Guacamole.ArrayBufferWriter} which
   * handles the per-chunk `ack` flow control with the server.
   *
   * Drag-over and drop events are bound to the host display element so users
   * can simply drag a file from the OS over the Guacamole canvas to upload it.
   * The `enable-drive` (RDP) parameter must be true on the server side for the
   * upload to land — that's gated by the per-class / per-resource VM Connection
   * Template overlay.
   *
   * @private
   */
  private setupFileUpload(): void {
    this.logger.d(`Setting up file upload handlers for session: ${this.sessionKey}, isFileTransferAllowed=${this.isFileTransferAllowed}`);

    if (!this.displayElement) return;

    // Track state so we don't kick off a new upload while another is mid-flight
    // for the same file (browsers can dispatch redundant `drop` events on
    // nested elements). Multiple distinct files dropped together are queued.
    if (null === this.dragOverListener) {
      this.dragOverListener = (event: DragEvent) => {
        // Indicate "copy" so the OS shows the right cursor.
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'copy';
        }
      };
    }

    if (null === this.dropListener) {
      this.dropListener = (event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return;
        this.logger.d(`Drag-drop: ${files.length} file(s) dropped onto VM session ${this.sessionKey}`);
        Array.from(files).forEach(file => this.uploadFile(file));
      };
    }

    // Capture phase so we win against any sibling listeners that might
    // call preventDefault before us.
    this.displayElement.addEventListener('dragover', this.dragOverListener, true);
    this.displayElement.addEventListener('drop', this.dropListener, true);
  }

  /**
   * Stream a single {@link File} into the remote VM via a Guacamole file stream.
   *
   * Reads the file as an ArrayBuffer and forwards it through
   * {@link Guacamole.ArrayBufferWriter}, which handles the per-chunk ack flow
   * control. Errors at any stage are logged and the stream is gracefully
   * closed; transient send errors during a long upload do not crash the page.
   *
   * Public so the VM Details drawer's "Upload File" button can trigger it
   * directly without requiring the user to drag the file onto the canvas.
   *
   * @param file - The browser {@link File} to upload
   */
  public uploadFile(file: File): void {
    if (!this.client) {
      this.logger.d(`Upload skipped — no active client for session ${this.sessionKey}`);
      return;
    }

    if (!this.isFileTransferAllowed) {
      this.logger.d(`Upload skipped — file transfer not allowed for session ${this.sessionKey}`);
      this.onFileUploadStart?.(file.name, file.size);
      return;
    }

    const mimetype = file.type || 'application/octet-stream';
    const stream = this.client.createFileStream(mimetype, file.name);
    const writer = new (Guacamole as any).ArrayBufferWriter(stream);
    this.logger.d(`Uploading file: ${file.name} (${file.size} bytes, ${mimetype})`);
    this.onFileUploadStart?.(file.name, file.size);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const buffer = reader.result as ArrayBuffer;
        writer.sendData(buffer);
        writer.sendEnd();
        this.logger.d(`Upload complete: ${file.name}`);
        this.onFileUploadComplete?.(file.name);
      } catch (err) {
        this.logger.e(`Upload failed for ${file.name}:`, err);
        this.onFileUploadError?.(file.name, err);
        try { writer.sendEnd(); } catch (ex) { this.logger.w('Failed to close file stream after upload error', ex);}
      }
    };
    reader.onerror = () => {
      this.logger.e(`FileReader failed for ${file.name}:`, reader.error);
      this.onFileUploadError?.(file.name, reader.error);
      try { writer.sendEnd(); } catch (ex) { this.logger.w('Failed to close file stream after FileReader error', ex);}
    };
    reader.readAsArrayBuffer(file);
  }

  /**
   * Disconnect the Guacamole client.
   *
   * @public
   */
  public disconnect(): void {
    if (this.client) {
      try {
        this.client.disconnect();
      } catch (error) {
        this.logger.e(`Error disconnecting client for session ${this.sessionKey}:`, error);
      }
    }
  }

  /**
   * Captures the current Guacamole display as a compressed JPEG thumbnail.
   * Scales the live canvas down to a fixed 320×180 viewport before encoding so the
   * resulting data URL size is bounded regardless of the actual VM resolution.
   *
   * @param width  Target width in pixels (default 320)
   * @param height Target height in pixels (default 180)
   * @param quality JPEG quality 0–1 (default 0.4 ≈ 8–15 KB)
   * @returns Base64 JPEG data URL string, or null if the canvas is not ready
   */
  public captureThumbnail(width = 320, height = 180, quality = 0.4): string | null {
    try {
      const displayEl = this.client?.getDisplay()?.getElement();
      if (!displayEl) return null;
      const srcCanvas = displayEl.querySelector('canvas') as HTMLCanvasElement | null;
      if (!srcCanvas || srcCanvas.width === 0 || srcCanvas.height === 0) return null;

      const thumb = document.createElement('canvas');
      thumb.width = width;
      thumb.height = height;
      const ctx = thumb.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(srcCanvas, 0, 0, width, height);
      return thumb.toDataURL('image/jpeg', quality);
    } catch {
      return null;
    }
  }

  /**
   * Check if the client is currently connected.
   *
   * @returns True if connected, false otherwise
   */
  public isClientConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Get the session key for this client instance.
   *
   * @returns The session key
   */
  public getSessionKey(): string {
    return this.sessionKey;
  }

  /**
   * Get the raw Guacamole client for advanced operations like sending keystrokes.
   *
   * @returns The Guacamole client instance
   */
  public getGuacamoleClient(): any {
    return this.client;
  }

  /**
   * Set up continuous monitoring and fixing of canvas styles
   * @private
   */
  private setupCanvasStyleFix(displayElement: HTMLElement): void {
    const fixCanvasStyles = () => {
      const canvas = displayElement.querySelector('canvas') as HTMLCanvasElement;
      if (canvas) {
        // Force correct styles
        canvas.style.setProperty('z-index', '1', 'important');
        canvas.style.setProperty('position', 'relative', 'important');
        canvas.style.setProperty('cursor', 'none', 'important');
      }
    };

    // Fix immediately
    fixCanvasStyles();

    // Set up mutation observer to catch style changes
    const observer = new MutationObserver(() => {
      fixCanvasStyles();
    });

    observer.observe(displayElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style']
    });

    // Also set up periodic fixing as backup
    const interval = setInterval(fixCanvasStyles, 500);

    // Store cleanup functions
    if (!this.canvasFixCleanup) {
      this.canvasFixCleanup = () => {
        observer.disconnect();
        clearInterval(interval);
      };
    }
  }

  private canvasFixCleanup?: () => void;

  /**
   * Comprehensive cleanup of all resources.
   * Call this when the component is destroyed or window is closed.
   *
   * @public
   */
  public cleanup(): void {
    this.logger.d(`Cleaning up Guacamole client for session: ${this.sessionKey}`);

    // Mark as intentional so the error/state handlers do not schedule a reconnect.
    this.isIntentionalDisconnect = true;

    // Delegate to the shared teardown helper.
    this.cleanupForReconnect();
  }
}
